import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Consistent Scripture-source choices (human descriptions). Proverbs is the default/heart.
export const SOURCE_OPTIONS = [
  { value: "proverbs", label: "Proverbs", desc: "Practical wisdom for life" },
  { value: "proverbs_psalms", label: "Proverbs + Psalms", desc: "Wisdom, comfort & encouragement" },
  { value: "wisdom", label: "All Biblical Wisdom", desc: "Relevant Scripture throughout the Bible" },
];

// The 10 most common concerns shown first; the rest live behind "See more concerns".
export const COMMON_CONCERN_SLUGS = [
  "anxiety", "stress", "relationships", "marriage", "financial-pressure",
  "family", "workplace-conflict", "grief", "decision", "sleep",
];

// Reusable progressive concern picker (up to `max`, common-first + "See more").
export function ConcernPicker({ topics, selected, onToggle, testidPrefix = "concern", max = 3 }) {
  const [expanded, setExpanded] = useState(false);
  const { common, rest } = useMemo(() => {
    const bySlug = Object.fromEntries(topics.map((t) => [t.slug, t]));
    const common = COMMON_CONCERN_SLUGS.map((s) => bySlug[s]).filter(Boolean);
    const commonSet = new Set(COMMON_CONCERN_SLUGS);
    const rest = topics.filter((t) => !commonSet.has(t.slug));
    return { common, rest };
  }, [topics]);

  const Chip = (t) => {
    const on = selected.includes(t.slug);
    return (
      <button key={t.slug} data-testid={`${testidPrefix}-${t.slug}`} onClick={() => onToggle(t.slug, max)}
        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${on ? "bg-[#2C4C3B] text-white" : "bg-[#F4F1EA] text-[#475569] hover:bg-[#EDE8DC]"}`}>
        {t.label}
      </button>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">{common.map(Chip)}</div>
      {expanded && <div className="flex flex-wrap gap-2 mt-2">{rest.map(Chip)}</div>}
      {rest.length > 0 && (
        <button data-testid={`${testidPrefix}-see-more`} onClick={() => setExpanded((v) => !v)} type="button"
          className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-[#2C4C3B] text-[#2C4C3B] text-base font-bold hover:bg-[#2C4C3B] hover:text-white transition-colors">
          {expanded ? "Show Fewer Concerns" : `See More Concerns (${rest.length} more)`}
          <ChevronDown className={`w-5 h-5 transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

// Shared toggle honoring the max-selection rule.
export function toggleConcern(list, slug, max = 3) {
  if (list.includes(slug)) return list.filter((x) => x !== slug);
  if (list.length >= max) { toast.info(`You can choose up to ${max}.`); return list; }
  return [...list, slug];
}

// Consistent source selector with descriptions.
export function SourceSelector({ value, onChange, testidPrefix = "source" }) {
  return (
    <div data-testid="source-selector" className="grid sm:grid-cols-3 gap-2">
      {SOURCE_OPTIONS.map((s) => {
        const on = value === s.value;
        return (
          <button key={s.value} data-testid={`${testidPrefix}-${s.value}`} onClick={() => onChange(s.value)}
            className={`text-left rounded-2xl border px-4 py-3 transition-colors ${on ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#0F172A]"}`}>
            <div className="text-sm font-semibold">{s.label}</div>
            <div className={`text-xs mt-0.5 ${on ? "text-white/70" : "text-[#94A3B8]"}`}>{s.desc}</div>
          </button>
        );
      })}
    </div>
  );
}
